import {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changePaymentMethod, paymentMethods } from '../../slices/payments';
import { calculateTotalMinusGiftCards } from '../../utils';
import useQueryParams from '../useQueryParams';

const usePaymentMethodsSelector = () => {
  const [
    stripePaymentIntentIdFromUrl,
  ] = useQueryParams([
    'payment_intent',
  ]);

  const dispatch = useDispatch();
  const [
    arePaymentMethodsDisabled,
    setPaymentMethodsDisabled,
  ] = useState(false);
  const {
    giftCards,
    totalPrice,
    taxedPrice,
  } = useSelector((state) => state.cart);
  const {
    selectedPaymentMethod,
    paypalOrderId,
    applePaymentData,
    amazonCheckoutSessionId,
  } = useSelector((state) => state.payments);

  const changeSelectedPaymentMethod = useCallback(
    (paymentMethod) => {
      dispatch(changePaymentMethod(paymentMethod));
    },
    [dispatch],
  );

  /**
   * There's a bug where, when coming back from a Stripe offsite payment, the
   * order is placed using only gift cards. It's a race condition between this
   * gift card payment method being selected and the Stripe order being
   * submitted. So for now, we just disable this if there is a Stripe intent
   * incoming.
   * @todo @kludge
   */
  const isCartPaidOnlyWithGiftCards = useMemo(
    () =>
      !stripePaymentIntentIdFromUrl &&
      calculateTotalMinusGiftCards({ taxedPrice, totalPrice, giftCards }) === 0,
    [taxedPrice, totalPrice, giftCards, stripePaymentIntentIdFromUrl],
  );

  useEffect(() => {
    if (isCartPaidOnlyWithGiftCards) {
      setPaymentMethodsDisabled(true);
      changeSelectedPaymentMethod(paymentMethods.GIFT_CARD);
    } else {
      setPaymentMethodsDisabled(false);
      if (selectedPaymentMethod === paymentMethods.GIFT_CARD) {
        changeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);
      }
    }
  }, [
    changeSelectedPaymentMethod,
    isCartPaidOnlyWithGiftCards,
    selectedPaymentMethod,
  ]);

  useEffect(() => {
    if (paypalOrderId) {
      changeSelectedPaymentMethod(paymentMethods.PAYPAL);
    }
    if (applePaymentData || localStorage.getItem('applePaymentData')) {
      changeSelectedPaymentMethod(paymentMethods.APPLE_PAY);
    }
    if (amazonCheckoutSessionId || localStorage.getItem('amazonCheckoutSessionId')) {
      changeSelectedPaymentMethod(paymentMethods.AMAZON_PAY);
    }
  }, [
    changeSelectedPaymentMethod,
    paypalOrderId,
    applePaymentData,
    amazonCheckoutSessionId,
  ]);

  return {
    changeSelectedPaymentMethod,
    arePaymentMethodsDisabled,
  };
};

export default usePaymentMethodsSelector;
